/**
 * Input validation & sanitization utilities.
 *
 * XSS prevention:  strip HTML tags, enforce character whitelists.
 * Form validation: phone (SG), email, password strength.
 */

// ─── Sanitization ───────────────────────────────────────────────────────────

/** Strip HTML tags to prevent XSS via user input. */
export function stripHtml(input: string): string {
  return input.replace(/<[^>]*>/g, "");
}

/** Escape HTML entities. */
export function escapeHtml(input: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };
  return input.replace(/[&<>"'/]/g, (char) => map[char] ?? char);
}

/** Remove non-printable / control characters. */
export function sanitizeInput(input: string): string {
  // eslint-disable-next-line no-control-regex
  return stripHtml(input).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

// ─── Validation ─────────────────────────────────────────────────────────────

/** Validate Singapore phone number (8 digits, starts with 8 or 9). */
export function isValidSGPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, "");
  return /^[89]\d{7}$/.test(cleaned);
}

/** Validate E.164 phone number (international). */
export function isValidE164(phone: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(phone);
}

/** Validate email format (basic RFC-ish check). */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Password strength result. */
export interface PasswordStrength {
  valid: boolean;
  errors: string[];
}

/**
 * Validate password strength.
 *
 * Rules:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special character
 */
export function validatePassword(password: string): PasswordStrength {
  const errors: string[] = [];
  if (password.length < 8) errors.push("Must be at least 8 characters");
  if (!/[A-Z]/.test(password)) errors.push("Must contain an uppercase letter");
  if (!/[a-z]/.test(password)) errors.push("Must contain a lowercase letter");
  if (!/\d/.test(password)) errors.push("Must contain a digit");
  if (!/[^A-Za-z0-9]/.test(password))
    errors.push("Must contain a special character");
  return { valid: errors.length === 0, errors };
}

/** Sanitize phone input: strip everything except digits and leading '+'. */
export function sanitizePhone(input: string): string {
  const leading = input.startsWith("+") ? "+" : "";
  return leading + input.replace(/\D/g, "");
}

/** Sanitize email input: trim whitespace, lowercase. */
export function sanitizeEmail(input: string): string {
  return sanitizeInput(input).trim().toLowerCase();
}
